import { useContext, useEffect, useState } from 'react';

import TelemetryContext from '@zr/components/context/TelemetryContext';

import { fetchNextQuestion, postAnswer } from './api';

export function useInterviewApi(encResponseId) {
  const { logInteraction } = useApplyFlowTelemetry();
  const [data, setData] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  // fetch first question from API
  useEffect(() => {
    const controller = new AbortController();
    async function fetchQuestion() {
      const json = await fetchNextQuestion(encResponseId, { signal: controller.signal });
      setData(json);
    }
    fetchQuestion().catch((error) => setErrorMessage(error.message));

    // cleanup function
    return () => controller.abort();
  }, [encResponseId, setData]);

  async function clearErrorMessage() {
    setErrorMessage('');
  }

  async function handleContinue(values, resetForm, setSubmitting) {
    await handleSubmit({ answers: values.answer, answerUpload: values.answerUpload }, resetForm);

    setSubmitting(false);
  }

  async function handleSkip(resetForm) {
    await handleSubmit({ skipQuestion: true }, resetForm);
  }

  async function handleSubmit(params, resetForm) {
    // reset error message
    setErrorMessage('');

    const postParams = {
      ...params,
    };

    if (data.num_suggestions) {
      postParams.numSuggestions = data.num_suggestions;
    }

    const questionId = data.questions[0].question_id;

    try {
      const json = await postAnswer(encResponseId, questionId, postParams);

      if (!json) { return; }

      // log interaction
      logInteraction(postParams.skipQuestion ? 'skip' : 'continue', {
        questionNumber: data.questions[0].number,
        totalQuestions: data.quiz_total_questions,
        completed: !!json.completed,
      });

      setData(json);
      // Reset form if there is still a question to answer.
      // Otherwise the previous answer will be pre-filled in the new question
      if (json.questions) {
        resetForm();
      }
    } catch (error) {
      // JS-9585 - display placeholder text if available instead of generic server error message.
      // The server returns a generic error message regardless of what the validation error was.
      // it was determined that making the server side changes(Question.pm) to include dynamically generated errors
      // was too expensive, so we are displaying the placeholder to give the user a better clue of what to fix.
      const errorMessageToDisplay = data?.questions?.[0].placeholder || error?.errors?.[0].error;
      setErrorMessage(errorMessageToDisplay);
    }
  }

  return {
    clearErrorMessage,
    data,
    errorMessage,
    handleContinue,
    handleSkip,
  };
}

export function useApplyFlowTelemetry() {
  // fallback to noop functions if TelemetryContext provider is not found higher
  // in the component tree
  return useContext(TelemetryContext) || { logView: () => {}, logInteraction: () => {} };
}
